import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Landmark, ArrowRight, CheckCircle2, Globe, Shield, Users, BarChart3, FileText, Mic } from "lucide-react";
import { SectionHeader } from "@/components/SectionComponents";

const features = [
  { icon: Users,     title: "Civil Service Training",            description: "Develop foreign language capabilities in public servants to improve citizen services and international cooperation." },
  { icon: Globe,     title: "Diplomatic Language Preparation",   description: "Intensive language programs for diplomats, consular staff, and international affairs professionals." },
  { icon: FileText,  title: "Language Policy Implementation",    description: "Support official language policies with scalable, cost-effective digital learning infrastructure." },
  { icon: Mic,       title: "Minority Language Preservation",    description: "Promote and preserve regional and minority languages through our unique +8,000 language network." },
  { icon: Shield,    title: "Government-Grade Security",         description: "Sovereign cloud hosting with full compliance for government security and data sovereignty requirements." },
  { icon: BarChart3, title: "Policy Impact Reporting",          description: "Evidence-based reports on language training outcomes for policy evaluation and budget justification." },
];

export default function Government() {
  return (
    <div className="min-h-screen bg-background">

      {/* Hero */}
      <section className="pt-28 pb-20 bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">For Governments</p>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground leading-tight mb-5">
              Multilingual Public Services for the Modern State
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed mb-8">
              Lingua2 helps government institutions build multilingual capacity — from front-line civil servants to senior diplomats — with AI-supported training at national scale.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary font-semibold transition-fast">
                <Link to="/contact">Contact Government Team <ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
              <Button asChild variant="outline" className="transition-fast">
                <Link to="/how-it-works">How It Works</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Government Solutions" title="Built for Public Institutions" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f) => (
              <div key={f.title} className="group p-6 rounded-xl border border-border bg-card card-hover hover:border-primary/25">
                <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center mb-4 group-hover:bg-primary/14 transition-fast">
                  <f.icon className="w-5 h-5 text-primary" strokeWidth={1.75} />
                </div>
                <h3 className="text-sm font-display font-semibold text-foreground mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
              <Link to="/contact">Request a Government Proposal <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Languages */}
      <section className="py-20 bg-surface">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-5xl font-display font-bold text-primary mb-2">8,000+</div>
          <h2 className="text-2xl font-display font-semibold text-foreground mb-4">Languages Supported</h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-8 max-w-xl mx-auto">
            Including minority, regional, and indigenous languages — Lingua2 is the only platform that supports language preservation alongside mainstream training.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {["English", "Spanish", "French", "Arabic", "Mandarin", "Russian", "Portuguese", "German", "Japanese", "Catalan", "Basque", "Welsh", "+ 7,988 more"].map((lang) => (
              <span key={lang} className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary/8 text-primary border border-primary/20">
                {lang}
              </span>
            ))}
          </div>
          <Button size="lg" asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
            <Link to="/contact">Speak to Our Government Team <ArrowRight className="ml-2 w-4 h-4" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
